import {toAbsoluteUrl} from '@/_metronic/helpers'
import {InputIconGroup} from '@/_metronic/partials/content/input-group/InputIconGroup'
import {FormikValues, useFormik} from 'formik'
import moment from 'moment'
import {FC, useEffect, useRef, useState} from 'react'
import {Button, Modal, Spinner} from 'react-bootstrap'
import DateRangePicker from 'react-bootstrap-daterangepicker'
import SVG from 'react-inlinesvg'
import {FormattedNumber, IntlProvider} from 'react-intl'
import {useParams} from 'react-router-dom'
import * as Yup from 'yup'
import {useBookingFormState} from '../core/FormStateProvider'
import {useSelectTreatmentModal} from '../core/_ModalProvider'
import {ProductPrice} from '../core/_models'
import {BookingSuccessModal} from './BookingSuccessModal'
import DropdownFacility from './DropdownFacility'
import {DropdownGender} from './DropdownGender'
import {SelectTreatmentModal} from './SelectTreatmentModal'

/** ------- helpers: WIB time + hours check (10:00 <= t < 22:00) ------- */
const TARGET_OUTLET_ID = '75d9b555-26ce-49af-bbf0-935bf9c9624f'

// --- helpers (place near your schema) ---
const OPEN_MINUTES = 10 * 60 // 10:00
const CLOSE_MINUTES = 22 * 60 // 22:00

const parseTimeToMinutes = (hhmm?: string) => {
  if (!hhmm) return NaN
  const [h, m] = hhmm.split(':').map(Number)
  if (Number.isNaN(h) || Number.isNaN(m)) return NaN
  return h * 60 + m
}

const isWithinWibRange = (hhmm?: string) => {
  const mins = parseTimeToMinutes(hhmm)
  if (Number.isNaN(mins)) return false
  return mins >= OPEN_MINUTES && mins <= CLOSE_MINUTES // use < CLOSE_MINUTES for exclusive 22:00
}

const guestFormSchemes = Yup.object().shape({
  name: Yup.string().nullable(),
  phone_number: Yup.string().nullable(),
  room_number: Yup.string().required('Nomor kamar tidak boleh kosong'),
  room_type: Yup.object()
    .shape({
      label: Yup.string(),
      value: Yup.string(),
    })
    .required('Jenis kamar tidak boleh kosong'),
  booking_date: Yup.string()
    .test('someTest', 'Tanggal booking tidak boleh sebelum sekarang', function (value, context) {
      const {booking_date} = context.parent
      if (moment(booking_date, 'DD MMMM yyyy').isBefore(moment(), 'days')) {
        return this.createError({message: 'Waktu booking harus lebih dari sekarang'})
      }

      return true
    })
    .required('Tanggal booking tidak boleh kosong'),
  gender: Yup.object()
    .shape({
      label: Yup.string(),
      value: Yup.string(),
    })
    .required('Jenis kelamin tidak boleh kosong'),
  booking_time: Yup.string()
    .required('Jam booking tidak boleh kosong')
    // 1) Always enforce 10:00–22:00 WIB
    // .test('wib-range', 'Jam operasional 10:00–22:00 WIB', (v) => (v ? isWithinWibRange(v) : false))
    // 2) If booking_date adalah HARI INI (WIB), jam tidak boleh sebelum sekarang (WIB)
    .test('not-before-now-wib', 'Waktu booking harus lebih dari sekarang', function (value) {
      const {booking_date} = this.parent as {booking_date?: string}
      if (!value || !booking_date) return true

      // Interpret tanggal & "value" pada zona WIB (UTC+07:00), tanpa geser ke UTC
      const selectedDateWIB = moment(booking_date, 'DD MMMM yyyy').utcOffset(7 * 60, true)
      const [h, m] = value.split(':').map(Number)
      if (Number.isNaN(h) || Number.isNaN(m)) return false

      const selectedDateTimeWIB = selectedDateWIB.clone().hour(h).minute(m).second(0)
      const nowWIB = moment().utcOffset(7 * 60)

      if (selectedDateTimeWIB.isSame(nowWIB, 'day')) {
        return selectedDateTimeWIB.isSameOrAfter(nowWIB)
      }
      return true
    }),
})

const guestFormInitialValues = {
  name: '',
  phone_number: '',
  room_number: '',
  room_type: null,
  booking_date: moment().format('DD MMMM yyyy'),
  booking_time: '',
  gender: null,
}

type Props = {
  outlet_id: string
  handleSubmit: (body: any) => void
}

const GuestForm: FC<Props> = ({outlet_id, handleSubmit}) => {
  const outletValidations = [
    '96f3d0d1-3a45-47bf-8099-72b5c73e8640',
    '45361e61-d301-4a1a-9c64-2b2e872186f4',
  ]
  const {id} = useParams()
  const shouldEnforceHours = id === TARGET_OUTLET_ID
  const {isLoading, setIsLoading} = useBookingFormState()
  const {showModal, show, showSuccess, showSuccessModal, closeModal, closeSuccessModal} =
    useSelectTreatmentModal()
  const [treatments, setTreatments] = useState<Array<ProductPrice>>([])
  const [oneTherapist, setOneTherapist] = useState(true)
  const [foundValidation, setFoundValidation] = useState(false)
  const [showClosedModal, setShowClosedModal] = useState(false)

  const dateParentEl = useRef<any>()

  useEffect(() => {
    if (!showSuccess) {
      handleReset(guestFormInitialValues)
      setTreatments([])
    }
  }, [showSuccess])

  useEffect(() => {
    var foundValidation = (outletValidations.find((item) => outlet_id === item) ?? []).length > 0

    setFoundValidation(foundValidation)
    if (foundValidation) {
      setFieldValue('room_type', {
        label: 'Cabin',
        value: 'cabin',
      })
    }
  }, [outlet_id])

  const onClickShowTreatment = () => {
    showModal()
  }

  const onSubmit = (values: FormikValues) => {
    // ✅ guard: outside 10:00–22:00 WIB?
    if (shouldEnforceHours && !isWithinWibRange(values.booking_time)) {
      setShowClosedModal(true)
      return // stop submission
    }

    const formattedPhone = values.phone_number
      ? values.phone_number.toString().startsWith('0')
        ? values.phone_number.toString()
        : '0' + values.phone_number.toString()
      : ''

    const body = {
      outlet_guid: outlet_id,
      employee_guid: '',
      customer_guid: '',
      customer_name: values.name,
      customer_phone: formattedPhone,
      customer_gender: values.gender?.value,
      type_order: 'guest hotel',
      room_number: parseInt(values.room_number),
      type_facility: values.room_type?.value,
      booking_date: moment(values.booking_date, 'DD MMMM yyyy').format('yyyy-MM-DD'),
      booking_time: moment(values.booking_time, 'HH:mm').format('HH:mm:ss'),
      notes: '',
      product: treatments?.map((x) => ({
        sku: x.sku,
        qty: x.qty,
      })),
      keluhan: '',
      is_send_notification: null,
      therapist_guid: '',
    }
    setIsLoading(true)
    handleSubmit(body)
  }

  useEffect(() => {
    if (!showSuccessModal) {
      handleReset(guestFormInitialValues)
    }
  }, [showSuccessModal])

  const {
    values,
    handleChange,
    handleBlur,
    touched,
    errors,
    handleReset,
    setFieldValue,
    handleSubmit: handleFormSubmit,
    setFieldTouched,
    isValid,
  } = useFormik({
    initialValues: guestFormInitialValues,
    validationSchema: guestFormSchemes,
    validateOnMount: true,
    onSubmit,
  })

  const isFormInvalid = !isValid || !treatments.length || isLoading

  // console.log(errors)

  // console.log(isValid)

  // console.log(values)

  const ClosedModal: FC<{show: boolean; onClose: () => void}> = ({show, onClose}) => (
    <Modal show={show} onHide={onClose} centered>
      <Modal.Header closeButton className='justify-content-center border-0 pb-0'>
        <Modal.Title className='w-100 text-center fw-bold fs-3'>We’re Closed</Modal.Title>
      </Modal.Header>

      <Modal.Body className='text-center'>
        {/* lock icon */}
        <SVG src={toAbsoluteUrl('/media/svg/general/lock.svg')} className='me-2 align-text-top' />

        {/* responsive illustration (fluid, centered, with a sensible max width) */}
        <img
          src={toAbsoluteUrl('/media/illustrations/sigma-1/5.png')}
          alt='Closed illustration'
          className='img-fluid d-block mx-auto mb-4'
          style={{maxWidth: 360, width: '100%', height: 'auto'}}
        />

        <p className='mt-3 mb-1'>
          Sorry, our operating hours are <b>10:00 – 22:00 WIB</b>.
        </p>
        <small className='text-muted'>Please select a time within operating hours.</small>
      </Modal.Body>

      <Modal.Footer>
        <Button variant='secondary' onClick={onClose}>
          OK
        </Button>
      </Modal.Footer>
    </Modal>
  )

  return (
    <>
      <form onSubmit={handleFormSubmit}>
        <div className='row'>
          <div className='col-12 mb-5'>
            <label htmlFor='name' className='form-label'>
              Options
            </label>
            <div className='d-flex align-items-center mt-3'>
              <label className='form-check form-check-inline form-check-solid me-5'>
                <input
                  className='form-check-input'
                  name='communication[]'
                  type='radio'
                  checked={oneTherapist}
                  onChange={() => {
                    setOneTherapist(true)
                    setTreatments([])
                  }}
                />
                <span className='fw-bold ps-2 fs-6'>1 Therapist</span>
              </label>
              <label className='form-check form-check-inline form-check-solid'>
                <input
                  className='form-check-input'
                  name='communication[]'
                  type='radio'
                  checked={!oneTherapist}
                  onChange={() => {
                    setOneTherapist(false)
                    setTreatments([])
                  }}
                />
                <span className='fw-bold ps-2 fs-6'>2 Therapist</span>
              </label>
            </div>
          </div>
          <div className='col-6'>
            <div className='mb-5'>
              <label htmlFor='name' className='form-label'>
                Nama <i className='text-muted'>(Optional)</i>
              </label>
              <InputIconGroup
                label=''
                placeholder='John doe'
                isValid={Boolean(values.name)}
                errorMessage={
                  !Boolean(values.name) && touched?.name ? errors?.name?.toString() : ''
                }
                value={values.name}
                onChange={handleChange('name')}
                onBlur={handleBlur('name')}
                icon='/media/svg/orders/user.svg'
                disabled={!outlet_id}
              />
            </div>
          </div>
          <div className='col-6'>
            <div className='mb-5'>
              <label htmlFor='phone' className='form-label'>
                No. HP <i className='text-muted'>(Optional)</i>
              </label>
              <InputIconGroup
                label=''
                placeholder='628000xxxxxx'
                isValid={Boolean(values.phone_number)}
                errorMessage={
                  !Boolean(values.phone_number) && touched?.phone_number
                    ? errors?.phone_number?.toString()
                    : ''
                }
                type='number'
                value={values.phone_number}
                onChange={handleChange('phone_number')}
                onBlur={handleBlur('phone_number')}
                icon='/media/svg/orders/phone.svg'
                disabled={!outlet_id}
              />
            </div>
          </div>
          <div className='col-6'>
            <div className='mb-5'>
              <InputIconGroup
                label='Nomor Kamar Hotel'
                placeholder='701'
                isValid={Boolean(values.room_number)}
                errorMessage={
                  !Boolean(values.room_number) && touched?.room_number
                    ? errors?.room_number?.toString()
                    : ''
                }
                value={values.room_number}
                onChange={handleChange('room_number')}
                onBlur={handleBlur('room_number')}
                icon='/media/svg/orders/receipt_square.svg'
                disabled={!outlet_id}
              />
            </div>
          </div>
          <div className='col-6'>
            <div className='mb-5'>
              <label htmlFor='room_type' className='required form-label'>
                Jenis Kamar
              </label>
              <DropdownFacility
                value={values.room_type}
                onBlur={handleBlur('room_type')}
                isDisabled={!outlet_id || foundValidation}
                onChange={(newValue) => {
                  setFieldValue('room_type', newValue)
                  setTimeout(() => {
                    setFieldTouched('room_type', !Boolean(newValue))
                  })
                }}
                options={[
                  {
                    label: 'Cabin',
                    value: 'cabin',
                  },
                  {
                    label: 'In Room',
                    value: 'in-room',
                  },
                ]}
              />
              {touched.room_type && errors.room_type && (
                <div className='fv-plugins-message-container'>
                  <div className='fv-help-block'>
                    <span role='alert'>{errors.room_type?.toString()}</span>
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className='col-12'>
            <div className='mb-5'>
              <label htmlFor='room_type' className='required form-label'>
                Jenis Kelamin
              </label>
              <DropdownGender
                currentValue={values.gender}
                isDisabled={!outlet_id}
                onChange={(newValue) => {
                  setFieldValue('gender', newValue)
                  setTimeout(() => {
                    setFieldTouched('gender', !Boolean(newValue))
                  })
                }}
                onBlur={handleBlur('gender')}
              />
              {touched.room_type && errors.room_type && (
                <div className='fv-plugins-message-container'>
                  <div className='fv-help-block'>
                    <span role='alert'>{errors.room_type?.toString()}</span>
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className='col-6'>
            <div className='mb-5 position-relative'>
              <label htmlFor='book_date' className='required form-label'>
                Tanggal Booking
              </label>
              <DateRangePicker
                initialSettings={{
                  singleDatePicker: true,
                  showDropdowns: true,
                  parentEl: '#booking-order-date',
                }}
                onCallback={(start) => {
                  setFieldValue('booking_date', start.format('DD MMMM yyyy'))
                }}
              >
                <div
                  className='d-flex align-items-center position-relative w-100'
                  id='booking-order-date'
                >
                  <InputIconGroup
                    type='text'
                    id='book_date'
                    placeholder={moment().format('DD MMMM yyyy')}
                    value={values.booking_date}
                    readOnly
                    icon='/media/svg/orders/calendar.svg'
                    disabled={!outlet_id}
                  />
                </div>
              </DateRangePicker>
              {touched.booking_date && errors.booking_date && (
                <div className='fv-plugins-message-container'>
                  <div className='fv-help-block'>
                    <span role='alert'>{errors.booking_date}</span>
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className='col-6'>
            <div className='mb-5'>
              <label htmlFor='book_date' className='required form-label'>
                Jam Booking
              </label>

              <div className='d-flex align-items-center position-relative w-100'>
                <InputIconGroup
                  type='time'
                  id='book_date'
                  placeholder={moment().format('HH:mm')}
                  value={values.booking_time}
                  onChange={(e) => setFieldValue('booking_time', e.target.value)}
                  onBlur={handleBlur}
                  icon='/media/svg/orders/clock.svg'
                  disabled={!Boolean(values.booking_date) || !outlet_id}
                />
              </div>
              {touched.booking_time && errors.booking_time && (
                <div className='fv-plugins-message-container'>
                  <div className='fv-help-block'>
                    <span role='alert'>{errors.booking_time}</span>
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className='col-12'>
            <div className='mb-5 d-flex flex-column'>
              <div className='d-flex justify-content-between align-items-center mb-3'>
                <label className='required form-label'>Treatment</label>
                <button
                  className='btn btn-icon bg-primary btn-sm text-white'
                  onClick={onClickShowTreatment}
                  type='button'
                  disabled={!outlet_id}
                >
                  <i className='bi-plus fs-3'></i>
                </button>
              </div>

              {treatments?.length === 0 && (
                <div className='btn btn-outline btn-outline-dashed btn-outline-default p-7 border-primary d-flex flex-row justify-content-center'>
                  <button
                    className='btn d-flex flex-row align-items-center gap-2'
                    onClick={onClickShowTreatment}
                    type='button'
                    disabled={!outlet_id}
                  >
                    <div className='btn btn-outline btn-outline btn-outline-default p-3 border-muted d-flex flex-row justify-content-center'>
                      <SVG className='fs-3' src={toAbsoluteUrl('/media/svg/orders/magicpen.svg')} />
                    </div>
                    <span className='ms-3 text-primary'>Pilih Treatment</span>
                  </button>
                </div>
              )}
              {treatments?.length > 0 && (
                <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4'>
                  {/* begin::Table head */}
                  <thead>
                    <tr className='fw-bold text-muted'>
                      <th className='min-w-150px'>Treatment</th>
                      <th className='min-w-140px'>Quantity</th>
                      <th className='min-w-120px'>Harga</th>
                      <th className='min-w-120px'>Aksi</th>
                    </tr>
                  </thead>
                  {/* end::Table head */}
                  {/* begin::Table body */}
                  <tbody>
                    {treatments.map((treat) => (
                      <tr key={treat.sku}>
                        <td>{treat?.product?.name}</td>
                        <td>
                          <div className='d-flex flex-row gap-3 align-items-center'>
                            <i
                              className='bi bi-dash-circle-fill fs-1'
                              style={{cursor: 'pointer'}}
                              onClick={(event) => {
                                if (!outlet_id) return
                                const qty = (treat?.qty || 0) - 1
                                if (qty < 1) {
                                  return
                                }
                                const treatment = treatments.map((x) => {
                                  if (x.sku === treat.sku) {
                                    return {...x, qty}
                                  }

                                  return x
                                })
                                setTreatments(treatment)
                              }}
                            ></i>
                            <span className='fs-3'>{treat?.qty || '0'}</span>
                            <i
                              className='bi bi-plus-circle-fill fs-1'
                              style={{cursor: 'pointer'}}
                              onClick={(event) => {
                                if (!outlet_id) return
                                const qty = (treat?.qty || 0) + 1
                                const treatment = treatments.map((x) => {
                                  if (x.sku === treat.sku) {
                                    return {...x, qty}
                                  }

                                  return x
                                })
                                setTreatments(treatment)
                              }}
                            ></i>
                          </div>
                        </td>
                        {/* <td>{treat.duration} menit</td> */}
                        <td>
                          <IntlProvider locale='id'>
                            Rp.{' '}
                            <FormattedNumber
                              value={(treat.price?.[0]?.sell_price || 0) * (treat.qty || 1)}
                            />
                          </IntlProvider>
                        </td>
                        <td>
                          <i
                            className='bi bi-x-lg fs-3 text-danger cursor-pointer'
                            onClick={() => {
                              if (!outlet_id) return
                              setTreatments((prev) => prev.filter((x) => x.sku !== treat.sku))
                            }}
                          ></i>
                        </td>
                      </tr>
                    ))}
                    <tr>
                      <td colSpan={2} className='text-end'>
                        Total
                      </td>
                      <td>
                        <IntlProvider locale='id'>
                          Rp.{' '}
                          <FormattedNumber
                            value={
                              treatments.reduce(
                                (partialSum, a) =>
                                  partialSum + (a.price?.[0]?.sell_price || 0) * (a.qty || 1),
                                0
                              ) || 0
                            }
                          />
                        </IntlProvider>
                      </td>
                    </tr>
                  </tbody>
                  {/* end::Table body */}
                </table>
              )}
            </div>
          </div>
          <div className='col-12'>
            <button
              className='btn btn-md btn-primary w-100'
              type='submit'
              disabled={isFormInvalid || !outlet_id}
            >
              {isLoading ? <Spinner animation='border' color='white' size='sm' /> : 'Book'}
            </button>
          </div>
        </div>
        <BookingSuccessModal
          onReserve={() => {
            // handleReset(null)
            // setTreatments([])
            closeSuccessModal()
          }}
        />
      </form>

      {/* 🔔 We're Closed modal */}
      <ClosedModal show={showClosedModal} onClose={() => setShowClosedModal(false)} />

      {show && (
        <SelectTreatmentModal
          outlet_id={outlet_id}
          ids='cobacoba'
          prevData={treatments}
          oneTherapist={!oneTherapist}
          onSave={(data: Array<ProductPrice>) => {
            setTreatments(data)
            closeModal()
          }}
        />
      )}
    </>
  )
}

export default GuestForm
